import React, { useState } from "react";
import {
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  Paper,
} from "@mui/material";
import { auth, db } from "../../firebase";
import {
  collection,
  serverTimestamp,
  query,
  where,
  getDocs,
  setDoc,
  doc,
} from "firebase/firestore";
import api from "../../services/api";

export default function InviteClientForm() {
  const [clientEmail, setClientEmail] = useState("");
  const [error, setError] = useState("");
  const [snack, setSnack] = useState({ open: false, message: "", type: "" });
  const [loading, setLoading] = useState(false);

  const validateEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleInvite = async () => {
    if (!clientEmail.trim()) {
      return setError("Email is required.");
    }

    if (!validateEmail(clientEmail)) {
      return setError("Please enter a valid email address.");
    }

    setError("");
    setLoading(true);

    try {
      const currentUser = auth.currentUser;
      const trimmedEmail = clientEmail.trim().toLowerCase();

      // Check for existing invite from this accountant
      const q = query(
        collection(db, "linkedClients"),
        where("clientEmail", "==", trimmedEmail),
        where("accountantUid", "==", currentUser.uid)
      );
      const existing = await getDocs(q);
      if (!existing.empty) {
        setSnack({
          open: true,
          message: "This client has already been invited.",
          type: "warning",
        });
        setLoading(false);
        return;
      }

      // Create Firestore doc (keyed by email for now — will be migrated on signup)
      const docRef = doc(collection(db, "linkedClients"));
      await setDoc(docRef, {
        accountantUid: currentUser.uid,
        clientEmail: trimmedEmail,
        clientUid: null,
        invitedAt: serverTimestamp(),
      });

      // Trigger invite email
      await api.post("/accountant/invite-client", {
        to: clientEmail,
        invitedBy: currentUser.email,
      });

      setSnack({
        open: true,
        message: "Client invite sent successfully!",
        type: "success",
      });
      setClientEmail("");
    } catch (err) {
      console.error(err);
      setSnack({
        open: true,
        message: "Failed to send invite.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 4 }}>
      <Typography variant="h6" gutterBottom>
        Invite a Client
      </Typography>
      <TextField
        fullWidth
        label="Client's Email"
        value={clientEmail}
        onChange={(e) => setClientEmail(e.target.value)}
        sx={{ mb: 2 }}
        error={!!error}
        helperText={error}
        disabled={loading}
      />
      <Button
        variant="contained"
        onClick={handleInvite}
        disabled={loading}
        sx={{
          backgroundColor: "#3b82f6",
          "&:hover": { backgroundColor: "#2563eb" },
        }}
      >
        {loading ? "Sending..." : "Send Invite"}
      </Button>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snack.type} variant="filled">
          {snack.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
